import { round } from 'lodash';
import type { Point } from '../../../../../common/maths/geometry/pointTypes';
import type { Rect } from '../../../../../common/maths/geometry/rect/rectTypes';

type SvgPathCommand = {
    command: string;
    values: number[];
};

export type StrokePoint = [number, number];

const COMMAND_TOKEN_REGEX = /([a-zA-Z])|([-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?)/g;
const PATH_TAG_REGEX = /<path\b[^>]*>/gi;
const ATTRIBUTE_REGEX = /([a-zA-Z_:][\w:.-]*)\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/g;

const COMMAND_PARAMETER_LENGTHS: Record<string, number> = {
    M: 2,
    L: 2,
    H: 1,
    V: 1,
    C: 6,
    S: 4,
    Q: 4,
    T: 2,
    A: 7,
    Z: 0,
};

const getFormattedPathNumber = (number: number) => {
    if (!Number.isFinite(number)) return '0';

    const rounded = round(number, 2);

    if (Object.is(rounded, -0)) return '0';

    return String(rounded);
};

const parsePathCommands = (pathData: string): SvgPathCommand[] | null => {
    const tokens = [...pathData.matchAll(COMMAND_TOKEN_REGEX)]
        .map((match) => match[1] || match[2])
        .filter((token): token is string => !!token);

    if (!tokens.length) return null;

    const commands: SvgPathCommand[] = [];
    let tokenIndex = 0;
    let currentCommand: string | null = null;

    while (tokenIndex < tokens.length) {
        const token = tokens[tokenIndex];
        const isCommandToken = /^[a-zA-Z]$/.test(token);

        if (isCommandToken) {
            currentCommand = token;
            tokenIndex += 1;
        }

        if (!currentCommand) return null;

        const commandName = currentCommand.toUpperCase();
        const parameterCount = COMMAND_PARAMETER_LENGTHS[commandName];

        if (parameterCount === undefined) return null;

        if (parameterCount === 0) {
            if (!isCommandToken) return null;

            commands.push({
                command: currentCommand,
                values: [],
            });

            continue;
        }

        if (tokenIndex + parameterCount > tokens.length) return null;

        const values: number[] = [];

        for (let index = 0; index < parameterCount; index += 1) {
            const parsedValue = Number.parseFloat(tokens[tokenIndex + index]);
            if (Number.isNaN(parsedValue)) return null;
            values.push(parsedValue);
        }

        commands.push({
            command: currentCommand,
            values,
        });

        tokenIndex += parameterCount;

        if (commandName === 'M') {
            currentCommand = currentCommand === 'M' ? 'L' : 'l';
        }
    }

    return commands;
};

const getTransformedPair = ({
    x,
    y,
    translation,
    scale,
    isRelative,
}: {
    x: number;
    y: number;
    translation: Point;
    scale: number;
    isRelative: boolean;
}) => {
    const scaledX = x * scale;
    const scaledY = y * scale;

    if (isRelative) {
        return {
            x: scaledX,
            y: scaledY,
        };
    }

    return {
        x: scaledX + translation.x,
        y: scaledY + translation.y,
    };
};

const transformCommandValues = ({
    command,
    values,
    scale,
    translation,
}: {
    command: string;
    values: number[];
    scale: number;
    translation: Point;
}): number[] => {
    const commandName = command.toUpperCase();
    const isRelative = command !== commandName;

    switch (commandName) {
        case 'M':
        case 'L':
        case 'T':
        case 'C':
        case 'S':
        case 'Q': {
            return values.map((value, index) => {
                if (index % 2 === 0) {
                    return getTransformedPair({
                        x: value,
                        y: values[index + 1],
                        translation,
                        scale,
                        isRelative,
                    }).x;
                }

                return getTransformedPair({
                    x: values[index - 1],
                    y: value,
                    translation,
                    scale,
                    isRelative,
                }).y;
            });
        }
        case 'H': {
            return values.map((value) => {
                const scaledValue = value * scale;
                return isRelative ? scaledValue : scaledValue + translation.x;
            });
        }
        case 'V': {
            return values.map((value) => {
                const scaledValue = value * scale;
                return isRelative ? scaledValue : scaledValue + translation.y;
            });
        }
        case 'A': {
            if (values.length !== 7) return values;

            const transformedEndpoint = getTransformedPair({
                x: values[5],
                y: values[6],
                translation,
                scale,
                isRelative,
            });

            return [
                values[0] * scale,
                values[1] * scale,
                values[2],
                values[3],
                values[4],
                transformedEndpoint.x,
                transformedEndpoint.y,
            ];
        }
        case 'Z':
        default:
            return values;
    }
};

const serializePathCommands = (commands: SvgPathCommand[]) =>
    commands
        .map(({ command, values }) => {
            if (!values.length) return command;

            const formattedValues = values.map(getFormattedPathNumber).join(' ');
            return `${command} ${formattedValues}`;
        })
        .join(' ');

const getPathTagAttributes = (pathTag: string) => {
    const attributes: Record<string, string> = {};

    for (const match of pathTag.matchAll(ATTRIBUTE_REGEX)) {
        const key = match[1].toLowerCase();
        const value = match[3] || match[4] || match[5] || '';

        attributes[key] = value;
    }

    return attributes;
};

const getAttribute = (attributes: Record<string, string>, keys: string[]) => {
    for (const key of keys) {
        const value = attributes[key];

        if (value !== undefined) return value;
    }

    return undefined;
};

const updateBounds = ({
    x,
    y,
    left,
    top,
    right,
    bottom,
}: {
    x: number;
    y: number;
    left: number;
    top: number;
    right: number;
    bottom: number;
}) => ({
    left: Math.min(left, x),
    top: Math.min(top, y),
    right: Math.max(right, x),
    bottom: Math.max(bottom, y),
});

const resolveCoord = (current: number, value: number, isRelative: boolean) => (isRelative ? current + value : value);

const getBoundsFromCommands = (commands: SvgPathCommand[]): Rect | undefined => {
    let currentX = 0;
    let currentY = 0;
    let startX = 0;
    let startY = 0;

    let left = Infinity;
    let top = Infinity;
    let right = -Infinity;
    let bottom = -Infinity;

    const applyBounds = (x: number, y: number) => {
        ({ left, top, right, bottom } = updateBounds({
            x,
            y,
            left,
            top,
            right,
            bottom,
        }));
    };

    commands.forEach(({ command, values }) => {
        const commandName = command.toUpperCase();
        const isRelative = command !== commandName;

        switch (commandName) {
            case 'M':
            case 'L':
            case 'T': {
                const nextX = resolveCoord(currentX, values[0], isRelative);
                const nextY = resolveCoord(currentY, values[1], isRelative);

                currentX = nextX;
                currentY = nextY;

                if (commandName === 'M') {
                    startX = nextX;
                    startY = nextY;
                }

                applyBounds(nextX, nextY);
                return;
            }
            case 'H': {
                const nextX = resolveCoord(currentX, values[0], isRelative);
                currentX = nextX;
                applyBounds(nextX, currentY);
                return;
            }
            case 'V': {
                const nextY = resolveCoord(currentY, values[0], isRelative);
                currentY = nextY;
                applyBounds(currentX, nextY);
                return;
            }
            case 'C': {
                const controlPoint1X = resolveCoord(currentX, values[0], isRelative);
                const controlPoint1Y = resolveCoord(currentY, values[1], isRelative);
                const controlPoint2X = resolveCoord(currentX, values[2], isRelative);
                const controlPoint2Y = resolveCoord(currentY, values[3], isRelative);
                const nextX = resolveCoord(currentX, values[4], isRelative);
                const nextY = resolveCoord(currentY, values[5], isRelative);

                applyBounds(controlPoint1X, controlPoint1Y);
                applyBounds(controlPoint2X, controlPoint2Y);
                applyBounds(nextX, nextY);

                currentX = nextX;
                currentY = nextY;
                return;
            }
            case 'S':
            case 'Q': {
                const controlPointX = resolveCoord(currentX, values[0], isRelative);
                const controlPointY = resolveCoord(currentY, values[1], isRelative);
                const nextX = resolveCoord(currentX, values[2], isRelative);
                const nextY = resolveCoord(currentY, values[3], isRelative);

                applyBounds(controlPointX, controlPointY);
                applyBounds(nextX, nextY);

                currentX = nextX;
                currentY = nextY;
                return;
            }
            case 'A': {
                const radiusX = Math.abs(values[0]);
                const radiusY = Math.abs(values[1]);
                const nextX = resolveCoord(currentX, values[5], isRelative);
                const nextY = resolveCoord(currentY, values[6], isRelative);

                applyBounds(currentX - radiusX, currentY - radiusY);
                applyBounds(currentX + radiusX, currentY + radiusY);
                applyBounds(nextX - radiusX, nextY - radiusY);
                applyBounds(nextX + radiusX, nextY + radiusY);

                currentX = nextX;
                currentY = nextY;
                return;
            }
            case 'Z': {
                currentX = startX;
                currentY = startY;
                applyBounds(currentX, currentY);
                return;
            }
            default:
                return;
        }
    });

    if (!Number.isFinite(left) || !Number.isFinite(top) || !Number.isFinite(right) || !Number.isFinite(bottom)) {
        return undefined;
    }

    return {
        x: left,
        y: top,
        top,
        right,
        bottom,
        left,
        width: right - left,
        height: bottom - top,
    };
};

export type ParsedDrawingSvgPath = {
    d: string;
    color?: string;
    strokeWidth?: number;
    isStrokeOnly: boolean;
};

// Turn the points returned from perfect-freehand into SVG path data.
export const getSvgPathFromStroke = (stroke: StrokePoint[], close?: boolean) => {
    if (!stroke.length) return '';

    if (!close && stroke.length < 2) return `M ${stroke[0][0]} ${stroke[0][1]} l 0 0`;

    const d = stroke.reduce<(string | number)[]>(
        (acc, [x0, y0], index, points) => {
            if (!close && index === stroke.length - 1) {
                acc.push('L', x0, y0);
                return acc;
            }

            const [x1, y1] = points[(index + 1) % points.length];
            acc.push(round(x0, 2), round(y0, 2), round((x0 + x1) / 2, 2), round((y0 + y1) / 2, 2));
            return acc;
        },
        ['M', ...stroke[0], 'Q'],
    );

    if (close) d.push('Z');

    return d.join(' ');
};

export const parseDrawingSvgPathTags = (svg: string | undefined | null): ParsedDrawingSvgPath[] => {
    if (!svg || typeof svg !== 'string') return [];

    const pathTags = svg.match(PATH_TAG_REGEX);

    if (!pathTags?.length) return [];

    return pathTags
        .map((pathTag) => {
            const attributes = getPathTagAttributes(pathTag);

            const d = getAttribute(attributes, ['d']);
            if (!d) return null;

            const className = getAttribute(attributes, ['class']) || '';
            const fillColor = getAttribute(attributes, ['fill']);
            const strokeColor = getAttribute(attributes, ['stroke']);
            const strokeWidthValue = getAttribute(attributes, ['stroke-width', 'strokewidth']);
            const strokeWidth = strokeWidthValue !== undefined ? Number.parseFloat(strokeWidthValue) : undefined;

            const isStrokeOnly =
                className.split(' ').includes('stroke-only') ||
                ((fillColor || '').toLowerCase() === 'none' && !!strokeColor);

            return {
                d,
                color: isStrokeOnly ? strokeColor : fillColor,
                strokeWidth: Number.isFinite(strokeWidth) ? strokeWidth : undefined,
                isStrokeOnly,
            } as ParsedDrawingSvgPath;
        })
        .filter((path): path is ParsedDrawingSvgPath => !!path);
};

export const tryTransformSvgPathData = ({
    pathData,
    scale = 1,
    translation = { x: 0, y: 0 },
}: {
    pathData: string;
    scale?: number;
    translation?: Point;
}) => {
    const commands = parsePathCommands(pathData);

    if (!commands) return undefined;

    const transformedCommands = commands.map(({ command, values }) => ({
        command,
        values: transformCommandValues({
            command,
            values,
            scale,
            translation,
        }),
    }));

    return serializePathCommands(transformedCommands);
};

export const transformSvgPathData = ({
    pathData,
    scale = 1,
    translation = { x: 0, y: 0 },
}: {
    pathData: string;
    scale?: number;
    translation?: Point;
}): string => {
    if (!pathData || typeof pathData !== 'string') return '';

    const transformedPathData = tryTransformSvgPathData({
        pathData,
        scale,
        translation,
    });

    if (transformedPathData) return transformedPathData;

    return pathData;
};

export const isValidSvgPathData = (pathData: string): boolean => {
    if (!pathData || typeof pathData !== 'string') return false;
    return !!parsePathCommands(pathData);
};

export const getSvgPathBoundingRect = (pathData: string): Rect | undefined => {
    if (!pathData || typeof pathData !== 'string') return undefined;

    const commands = parsePathCommands(pathData);

    if (!commands) return undefined;

    return getBoundsFromCommands(commands);
};
